<template>
  <div id="app">
    <!-- Web : en-tête historique, inchangé. Natif : barre d'application. -->
    <AppHeader v-if="!isNative" />
    <OcAppBar v-else />

    <div v-if="isNative && isOffline" class="oc-offline">
      Pas de connexion internet
    </div>

    <div class="page-main">
      <!-- Web : rendu direct, inchangé. Natif : les écrans entrent et sortent
           avec une direction, comme dans une application. -->
      <router-view v-if="!isNative" />
      <router-view v-else v-slot="{ Component, route }">
        <!-- L'enveloppe est indispensable : la plupart des écrans ont
             plusieurs éléments racines (Vue 3 autorise les fragments), et
             <transition> ne sait animer qu'une racine unique — sans elle, la
             page s'affichait entièrement vide. La clé force le remplacement
             de l'écran à chaque changement d'adresse. -->
        <transition :name="navTransition" mode="out-in">
          <div
            :key="route.fullPath"
            class="oc-screen"
            :class="`oc-screen--${String(route.name || 'inconnu')}`"
          >
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </div>

    <!-- Web : pied de page légal complet. Natif : barre d'onglets. -->
    <AppFooter v-if="!isNative" />
    <OcTabBar v-if="isNative" />

    <div v-if="!isNative && !cookieAccepted" class="cookie-bar" id="cookie-bar">
        <div class="row">
          <div class="col-12 col-sm-8 text-left align-self-center">
            En naviguant sur notre site, vous acceptez l’utilisation de cookies qui nous permettront de réaliser des analyses et de vous proposer des contenus adaptés pour améliorer votre expérience.
          </div>
          <div class="col-12 col-sm-4 align-self-center text-right">
            <button @click="acceptCookies" class="btn btn-default close-cookie-bar">Oui, j'accepte</button><br>
            <a href="https://www.ouicsport.fr/politique-de-confidentialite" target="_blank">Gestion de la vie privée</a>
          </div>
        </div>
    </div>

  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import OcAppBar from '@/components/native/OcAppBar.vue';
import OcTabBar from '@/components/native/OcTabBar.vue';
import { initPushNotifications } from '@/native/push';
import { initDeepLinks } from '@/native/deep-link';
import { initBackButton } from '@/native/back-button';
import { initStatusBar } from '@/native/status-bar';
import { hideSplash } from '@/native/splash';
import { isNative } from '@/api/base';

/**
 * Profondeur de chaque écran dans l'application.
 *
 * Elle donne la direction de l'animation : on ne « pousse » pas un écran de la
 * même façon selon qu'on entre dans un détail ou qu'on en ressort. Une carte
 * fondée sur les noms de routes est préférable à une lecture de l'historique
 * du navigateur : elle reste juste quand l'écran est ouvert par un lien
 * profond ou par une notification, cas où il n'y a aucun historique.
 */
const SCREEN_LEVELS = {
  // Écrans racines, atteints par la barre d'onglets
  HomePage: 0,
  OffersList: 0,
  Gallery: 0,
  AccountPage: 0,
  LoginPage: 0,
  // Premier niveau
  OfferDetailsPage: 1,
  Contact: 1,
  MyOffers: 1,
  Profile: 1,
  CGU: 1,
  mentionslegales: 1,
  politiqueConfidentialite: 1,
  ForgotPasswordPage: 1,
  ResetPasswordPage: 1,
  // Tunnel de réservation
  OfferDetailsPageV2: 2,
  ConfirmationPage: 3,
  BookingSuccess: 3,
};

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    OcAppBar,
    OcTabBar,
  },
  data() {
    return {
      cookieAccepted: false,
      isNative,
      isOffline: false,
      navTransition: 'oc-nav-fade',
    };
  },
  created() {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith('cookiesAccepted='));
    if (cookie) {
      this.cookieAccepted = true;
    }

    if (isNative) {
      // Écrans réservés au site : le back-office d'images et la page de test
      // n'ont rien à faire dans une application distribuée sur les magasins,
      // d'autant que les endpoints de la galerie ne sont pas authentifiés
      // côté serveur. On les renvoie vers l'accueil.
      const HORS_APP = ['AdminGestionImagesOuicsport', 'test'];

      // Écrans qui exigent une session. Sans cette garde, l'écran compte
      // plante à l'ouverture (`store.state.auth.user.data.id` est lu sans
      // protection) et l'utilisateur se retrouve devant une page blanche —
      // ce qui arrive dès que la session de 24h a expiré, ou en arrivant par
      // un lien profond. La garde vit ici, côté application : le composant
      // partagé avec le site n'est pas modifié.
      const AVEC_SESSION = ['AccountPage', 'Profile', 'MyOffers', 'OfferDetailsPageV2'];

      this.$router.beforeEach((to) => {
        if (HORS_APP.includes(to.name)) return { name: 'HomePage' };
        if (AVEC_SESSION.includes(to.name) && !this.$store.state.auth?.user) {
          return { name: 'LoginPage' };
        }
        return true;
      });

      this.$router.beforeEach((to, from) => {
        const level = (r) => SCREEN_LEVELS[r.name] ?? 1;
        const delta = level(to) - level(from);
        // Aller vers un écran plus profond pousse par la droite, revenir tire
        // vers la droite, et changer d'onglet se fait par fondu : une barre
        // d'onglets ne glisse pas latéralement.
        this.navTransition = delta > 0 ? 'oc-nav-forward'
          : delta < 0 ? 'oc-nav-back'
            : 'oc-nav-fade';
      });
    }
  },
  mounted() {
    // La classe `is-native` est posée dès main.js, avant le montage.
    initStatusBar();
    initPushNotifications(this.$store, this.$router);
    initDeepLinks(this.$router);
    initBackButton(this.$router);

    // Sans réseau, les écrans restaient vides sans explication : les appels
    // échouaient en silence. Le bandeau nomme la cause.
    if (isNative) {
      this.isOffline = navigator.onLine === false;
      window.addEventListener('online', this.handleNetworkChange);
      window.addEventListener('offline', this.handleNetworkChange);
    }

    // La première vue est montée : on peut retirer l'écran de lancement.
    this.$nextTick(hideSplash);
  },
  beforeUnmount() {
    window.removeEventListener('online', this.handleNetworkChange);
    window.removeEventListener('offline', this.handleNetworkChange);
  },
  methods: {
    acceptCookies() {
      document.cookie = 'cookiesAccepted=true; path=/; max-age=31536000';
      this.cookieAccepted = true;
    },
    handleNetworkChange() {
      this.isOffline = navigator.onLine === false;
    },
  },
};
</script>

<style>
.cookie-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #dd4124;
  color: #fff;
  text-align: center;
  z-index: 9999;
  padding: 30px 60px;
  transition: all 1s ease;
  /* Pour éviter qu’une ligne trop longue ne déborde, on autorise le passage à la ligne automatique */
  word-break: break-word;
}

.cookie-bar a,
.cookie-bar a:hover {
  color: #fff;
  text-decoration: underline;
}

.cookie-bar .btn.btn-default,
.cookie-bar .btn.btn-default:hover {
  color: #dd4124 !important;
  background-color: #fff !important;
  margin-top: 0;
}

/* === Grille de base === */
.container {
  max-width: 1200px;
  margin: auto;
}

.row {
  display: flex;
  align-items: center;
  flex-wrap: wrap; /* Important pour les petits écrans */
}

/* Colonne qui fait 100% par défaut (mobile) */
.col-12 {
  width: 100%;
}

/* À partir de 576px, on applique la grille “sm” */
.col-sm-8 {
  flex: 0 0 auto;
  width: 66.66667%;
  max-width: 66.66667%;
  text-align: left;
}

.col-sm-4 {
  flex: 0 0 auto;
  width: 33.33333%;
  max-width: 33.33333%;
  text-align: center;
}

/* =========================================================
   Version Mobile (<= 576px)
   ---------------------------------------------------------
   - On passe les deux colonnes en 100 % de largeur.
   - On centre le texte et le bouton.
   - On adapte les paddings et la taille de police.
   - On force la colonne des boutons à avoir un margin-top.
   ========================================================= */
@media (max-width: 576px) {
  .cookie-bar {
    padding: 8px 20px;       /* On réduit un peu le padding horizontal */
    font-size: 14px;         /* On baisse légèrement la taille de police */
  }

  .row {
    flex-direction: column;  /* Empilement vertical */
    text-align: center;      /* Centrer tout le contenu */
  }

  .col-sm-8,
  .col-sm-4 {
    flex: 0 0 100%;
    max-width: 100%;
    text-align: center;      /* Centrage du texte dans chaque colonne */
  }

  .col-sm-4 {
    margin-top: 10px;        /* Espacement au-dessus du bouton/ lien */
  }

  /* Si le bouton ou le lien était en text-right, on force le centrage */
  .cookie-bar .text-right {
    text-align: center !important;
  }
}

</style>
